import { useState } from 'react'

import { Box, Button, Checkbox, Section, Text } from '@factorialco/deprecated-design-system'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceAccrualModeSettingDocument,
  type SaveSalaryAdvanceAccrualModeSettingMutation,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES,
  ACCRUAL_MODE_ATTENDANCE_CONNECTED,
  ACCRUAL_MODE_CALENDAR_ONLY,
  type AttendanceEligibleSalaryFrequency,
  type AccrualMode,
  isAccrualMode,
} from 'modules/salary_advance/lib/companySettings'

const frequencyLabel = (frequency: AttendanceEligibleSalaryFrequency): string => {
  switch (frequency) {
    case 'hourly':
      return i18n.t('common.hourly')
    case 'daily':
      return i18n.t('common.daily')
    case 'weekly':
      return i18n.t('common.weekly')
    case 'monthly':
      return i18n.t('common.monthly')
    case 'yearly':
      return i18n.t('common.yearly')
    default: {
      const _exhaustive: never = frequency
      return String(_exhaustive)
    }
  }
}

type Props = {
  accrualMode: string
  attendanceAccrualSalaryFrequencies: AttendanceEligibleSalaryFrequency[]
  unsupportedAttendanceAccrualSalaryFrequencies: string[]
  timeTrackingAvailableForAccrualMode: boolean
  canEdit: boolean
}

const AccrualModeSetting = ({
  accrualMode,
  attendanceAccrualSalaryFrequencies,
  unsupportedAttendanceAccrualSalaryFrequencies,
  timeTrackingAvailableForAccrualMode,
  canEdit,
}: Props) => {
  const [saveAccrualMode, { loading }] = useMutation(SaveSalaryAdvanceAccrualModeSettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceAccrualModeSettingMutation) =>
      payload.salaryAdvanceMutations?.saveAccrualModeSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
    awaitRefetchQueries: true,
  })
  const [isConfirmationOpen, setConfirmationOpen] = useState(false)
  const [pendingAccrualMode, setPendingAccrualMode] = useState<AccrualMode | null>(null)

  const currentAccrualMode = isAccrualMode(accrualMode) ? accrualMode : null
  const hasUnsupportedAccrualMode = currentAccrualMode === null
  const isAttendanceConnected = currentAccrualMode === ACCRUAL_MODE_ATTENDANCE_CONNECTED
  const buildPersistedFrequencies = (
    supportedFrequencies: AttendanceEligibleSalaryFrequency[]
  ): string[] => {
    const supportedSet = new Set(supportedFrequencies)

    return [
      ...ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES.filter((value) => supportedSet.has(value)),
      ...unsupportedAttendanceAccrualSalaryFrequencies,
    ]
  }

  const handleToggle = (enabled: boolean) => {
    if (!currentAccrualMode) return

    const nextValue = enabled ? ACCRUAL_MODE_ATTENDANCE_CONNECTED : ACCRUAL_MODE_CALENDAR_ONLY

    if (nextValue === currentAccrualMode) return

    if (nextValue === ACCRUAL_MODE_ATTENDANCE_CONNECTED && !timeTrackingAvailableForAccrualMode) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('salary_advance.settings.accrual_mode.requires_time_tracking'),
      })
      return
    }

    setPendingAccrualMode(nextValue)
    setConfirmationOpen(true)
  }

  const closeConfirmation = () => {
    setConfirmationOpen(false)
    setPendingAccrualMode(null)
  }
  const showFallbackError = (errors: ReadonlyArray<{ __typename?: string }>) => {
    const hasSimpleError = errors.some((error) => error.__typename === 'SimpleError')

    if (!hasSimpleError) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const handleConfirm = async () => {
    if (!pendingAccrualMode) return

    const result = await saveAccrualMode({
      variables: {
        accrualMode: pendingAccrualMode,
      },
    })

    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.settingsSaveResult, {
      setting_key: 'accrual_mode',
      setting_value: pendingAccrualMode,
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closeConfirmation()
    } else {
      showFallbackError(handledResult.errors)
    }
  }

  const handleFrequencyToggle = async (
    frequency: AttendanceEligibleSalaryFrequency,
    checked: boolean
  ) => {
    if (!isAttendanceConnected || !canEdit || loading) return

    const currentSet = new Set(attendanceAccrualSalaryFrequencies)
    if (checked) {
      currentSet.add(frequency)
    } else {
      currentSet.delete(frequency)
    }

    const nextFrequencies = ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES.filter((value) =>
      currentSet.has(value)
    )
    const persistedFrequencies = buildPersistedFrequencies(nextFrequencies)

    if (nextFrequencies.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('salary_advance.settings.accrual_mode.minimum_frequency_required'),
      })
      return
    }

    const result = await saveAccrualMode({
      variables: {
        accrualMode: ACCRUAL_MODE_ATTENDANCE_CONNECTED,
        attendanceAccrualSalaryFrequencies: persistedFrequencies,
      },
    })

    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.settingsSaveResult, {
      setting_key: 'attendance_accrual_salary_frequencies',
      setting_value: persistedFrequencies.join(','),
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      showFallbackError(handledResult.errors)
    }
  }

  const modeControlDisabled =
    !canEdit ||
    loading ||
    hasUnsupportedAccrualMode ||
    (!timeTrackingAvailableForAccrualMode && !isAttendanceConnected)
  const frequencyControlDisabled =
    !canEdit ||
    loading ||
    hasUnsupportedAccrualMode ||
    !isAttendanceConnected ||
    !timeTrackingAvailableForAccrualMode
  const accrualModeDescription =
    currentAccrualMode === ACCRUAL_MODE_ATTENDANCE_CONNECTED
      ? i18n.t('salary_advance.settings.accrual_mode.attendance_description')
      : currentAccrualMode === ACCRUAL_MODE_CALENDAR_ONLY
        ? i18n.t('salary_advance.settings.accrual_mode.calendar_description')
        : currentAccrualMode === null
          ? i18n.t('salary_advance.settings.accrual_mode.unsupported_mode')
          : null

  return (
    <>
      <Section.Form
        illustration="clock-app"
        title={i18n.t('salary_advance.settings.accrual_mode.title')}
        description={i18n.t('salary_advance.settings.accrual_mode.description')}
        isDirty={false}
      >
        <Box flexDirection="column" gap="s12">
          <Box flexDirection="row" alignItems="center" gap="s12">
            <Text
              size="200"
              className={
                isAttendanceConnected ? 'text-f1-foreground-secondary' : 'text-f1-foreground'
              }
            >
              {i18n.t('salary_advance.settings.accrual_mode.calendar_label')}
            </Text>
            <Switch
              id="accrual-mode-toggle"
              checked={isAttendanceConnected}
              disabled={modeControlDisabled}
              title={i18n.t('salary_advance.settings.accrual_mode.title')}
              onCheckedChange={handleToggle}
            />
            <Text
              size="200"
              className={
                isAttendanceConnected ? 'text-f1-foreground' : 'text-f1-foreground-secondary'
              }
            >
              {i18n.t('salary_advance.settings.accrual_mode.attendance_label')}
            </Text>
          </Box>

          {accrualModeDescription && (
            <Box background="malibu200" borderRadius={{ all: 'abs008' }} padding="s8">
              <Text size="100" className="text-f1-foreground">
                {accrualModeDescription}
              </Text>
            </Box>
          )}

          <Box flexDirection="column" gap="s8">
            <Text size="100" color="grey700">
              {i18n.t('salary_advance.settings.accrual_mode.apply_to_label')}
            </Text>

            <Box flexDirection="column" gap="s6">
              {ACCRUAL_ATTENDANCE_ELIGIBLE_FREQUENCIES.map((frequency) => (
                <Checkbox
                  key={frequency}
                  size="medium"
                  disabled={frequencyControlDisabled}
                  checked={attendanceAccrualSalaryFrequencies.includes(frequency)}
                  label={frequencyLabel(frequency)}
                  onChange={(event) => handleFrequencyToggle(frequency, event.target.checked)}
                />
              ))}
            </Box>
          </Box>

          {!timeTrackingAvailableForAccrualMode && (
            <Box background="champagne200" borderRadius={{ all: 'abs008' }} padding="s12">
              <Text size="100" className="text-f1-foreground-secondary">
                {i18n.t('salary_advance.settings.accrual_mode.requires_time_tracking')}
              </Text>
            </Box>
          )}
        </Box>
      </Section.Form>

      {isConfirmationOpen && (
        <Modal
          illustration="alert"
          title={i18n.t('salary_advance.settings.accrual_mode.confirmation_title')}
          description={i18n.t('salary_advance.settings.accrual_mode.confirmation_description')}
          onClose={closeConfirmation}
          footer={
            <>
              <Button type="secondary" onClick={closeConfirmation}>
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={handleConfirm} disabled={loading}>
                {i18n.t('common.confirm')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default AccrualModeSetting
